import { useEffect, useState, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { useCare } from '../contexts/CareContext'
import { useToast, humanizeError } from '../components/shared/Toast'
import LoadingSpinner from '../components/shared/LoadingSpinner'
import CareLogForm from '../components/care-log/CareLogForm'
import VitalsForm from '../components/vitals/VitalsForm'
import IncidentForm from '../components/incidents/IncidentForm'
import { SHIFTS, currentShift, tasksForShift, todayISO } from '../lib/tasks'

const SHIFT_CHIPS = SHIFTS.filter((s) => s.value !== 'any')

/**
 * Caregiver home — one shift, one checklist, one way to log it.
 *
 * Deliberately not the family dashboard: a caregiver on shift needs their
 * tasks and a log button, not goals and trend charts.
 */
export default function CaregiverHomePage() {
  const { user } = useAuth()
  const { recipient } = useCare()
  const toast = useToast()

  const [loading, setLoading] = useState(true)
  const [tasks, setTasks] = useState([])
  const [templates, setTemplates] = useState([])
  const [shift, setShift] = useState(currentShift())
  const [busyId, setBusyId] = useState(null)
  const [form, setForm] = useState(null) // 'care-log' | 'vitals' | 'incident'
  const [logged, setLogged] = useState(false)

  const name = recipient?.preferred_name || recipient?.full_name || 'your client'

  const load = useCallback(async () => {
    if (!recipient) return
    const [taskRes, tplRes] = await Promise.all([
      supabase.from('tasks').select('*')
        .eq('care_recipient_id', recipient.id).eq('due_date', todayISO())
        .order('created_at', { ascending: true }),
      supabase.from('task_templates').select('title, shift')
        .eq('care_recipient_id', recipient.id).eq('is_active', true),
    ])
    setTasks(taskRes.data || [])
    setTemplates(tplRes.data || [])
    setLoading(false)
  }, [recipient])

  useEffect(() => { load() }, [load])

  async function toggleTask(task) {
    if (busyId) return
    const next = !task.is_completed
    setBusyId(task.id)
    setTasks((list) => list.map((t) => (t.id === task.id ? { ...t, is_completed: next } : t)))
    const { error } = await supabase.from('tasks').update({
      is_completed: next,
      completed_at: next ? new Date().toISOString() : null,
      completed_by: next ? user.id : null,
    }).eq('id', task.id)
    setBusyId(null)
    if (error) {
      setTasks((list) => list.map((t) => (t.id === task.id ? { ...t, is_completed: !next } : t)))
      toast.error(humanizeError(error))
    }
  }

  if (!recipient || loading) return <LoadingSpinner label="Loading your shift…" />

  const shown = tasksForShift(tasks, templates, shift)
  const done = shown.filter((t) => t.is_completed).length

  if (logged) {
    return (
      <div className="stack center" style={{ paddingTop: 40 }}>
        <div
          aria-hidden
          style={{
            width: 64, height: 64, borderRadius: '50%',
            background: 'var(--green-light)', color: 'var(--green)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 34, fontWeight: 700, lineHeight: 1,
          }}
        >
          ✓
        </div>
        <h1 className="title" style={{ fontSize: 24 }}>Shift logged</h1>
        <p style={{ fontSize: 15, lineHeight: 1.6, textAlign: 'center' }}>
          Today&rsquo;s summary has been sent to the family.
        </p>
        <button className="btn btn-block btn-lg" onClick={() => { setLogged(false); load() }}>Done</button>
      </div>
    )
  }

  return (
    <div className="stack">
      <div className="row" style={{ gap: 10 }}>
        {recipient.photo_url ? (
          <img
            src={recipient.photo_url} alt={recipient.full_name}
            style={{ width: 46, height: 46, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
          />
        ) : (
          <div
            aria-hidden
            style={{
              width: 46, height: 46, borderRadius: '50%', flexShrink: 0,
              background: 'var(--pa)', color: '#fff', fontWeight: 700, fontSize: 19,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {(recipient.full_name || '?').charAt(0).toUpperCase()}
          </div>
        )}
        <h1 className="title" style={{ fontSize: 24 }}>{name}</h1>
      </div>

      <div className="row wrap" style={{ gap: 8 }}>
        {SHIFT_CHIPS.map((s) => (
          <button
            key={s.value} type="button"
            className={`chip${shift === s.value ? ' chip-on' : ''}`}
            aria-pressed={shift === s.value}
            onClick={() => setShift(s.value)}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="spread">
        <div className="section-title">My tasks for this shift</div>
        {shown.length > 0 && (
          <span className="muted" style={{ fontSize: 13 }}>{done}/{shown.length}</span>
        )}
      </div>

      {shown.length === 0 ? (
        <div className="card muted" style={{ fontSize: 14 }}>
          No tasks scheduled for the {shift} shift today.
        </div>
      ) : (
        <div className="stack" style={{ gap: 8 }}>
          {shown.map((t) => (
            <button
              key={t.id}
              onClick={() => toggleTask(t)}
              className="card"
              style={{
                width: '100%', textAlign: 'left', minHeight: 56,
                display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer',
              }}
            >
              <span
                aria-hidden
                style={{
                  width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                  border: t.is_completed ? '2px solid var(--green)' : '2px solid var(--border)',
                  background: t.is_completed ? 'var(--green)' : 'transparent',
                  color: '#fff', fontSize: 14,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                {t.is_completed ? '✓' : ''}
              </span>
              <span
                style={{
                  fontSize: 16, fontWeight: 600, lineHeight: 1.4,
                  textDecoration: t.is_completed ? 'line-through' : 'none',
                  opacity: t.is_completed ? 0.55 : 1,
                }}
              >
                {t.title}
              </span>
            </button>
          ))}
        </div>
      )}

      <button className="btn btn-lg btn-block" onClick={() => setForm('care-log')}>
        Start shift log →
      </button>

      <div className="section-title">Quick log</div>
      <div className="row wrap" style={{ gap: 8 }}>
        <button className="btn btn-sm btn-secondary grow" onClick={() => setForm('care-log')}>📝 Care note</button>
        <button className="btn btn-sm btn-secondary grow" onClick={() => setForm('incident')}>⚠️ Incident</button>
        <button className="btn btn-sm btn-secondary grow" onClick={() => setForm('vitals')}>🩺 Vitals</button>
      </div>

      {form === 'care-log' && (
        <div className="modal-scrim" onClick={(e) => e.target === e.currentTarget && setForm(null)}>
          <div className="modal-panel">
            <CareLogForm
              recipientId={recipient.id}
              initialShift={shift}
              initialLoggerType="caregiver"
              onCancel={() => setForm(null)}
              onDone={() => { setForm(null); setLogged(true) }}
            />
          </div>
        </div>
      )}
      {form === 'vitals' && (
        <div className="modal-scrim" onClick={(e) => e.target === e.currentTarget && setForm(null)}>
          <div className="modal-panel">
            <VitalsForm recipientId={recipient.id} onCancel={() => setForm(null)} onDone={() => setForm(null)} />
          </div>
        </div>
      )}
      {form === 'incident' && (
        <div className="modal-scrim" onClick={(e) => e.target === e.currentTarget && setForm(null)}>
          <div className="modal-panel">
            <IncidentForm recipientId={recipient.id} onCancel={() => setForm(null)} onDone={() => setForm(null)} />
          </div>
        </div>
      )}

      <div style={{ height: 8 }} />
    </div>
  )
}
